/**
 * Product Gallery Component (Vanilla JS)
 * Handles Swiper initialization and fullscreen toggle for product gallery
 */
;(function () {
	'use strict'

	/**
	 * Initialize gallery when DOM is ready
	 * Wait a bit to ensure Swiper is loaded from tailwind.ts
	 */
	function waitForSwiper() {
		if (typeof window.Swiper !== 'undefined') {
			init()
		} else {
			setTimeout(waitForSwiper, 50)
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', waitForSwiper)
	} else {
		waitForSwiper()
	}

	function init() {
		initGallerySwiper()
		initGalleryToggle()
	}

	/**
	 * Initialize product gallery Swiper instances
	 */
	function initGallerySwiper() {
		// Check if Swiper library is available
		if (typeof window.Swiper === 'undefined') {
			console.warn('Swiper library not found. Gallery functionality will not work.')
			return
		}

		const Swiper = window.Swiper

		const thumbsContainer = document.querySelector('.gallery__thumbs')
		const fullContainer = document.querySelector('.gallery__full')

		if (!thumbsContainer || !fullContainer) {
			return // Not on a product page with gallery
		}

		// Count slides to determine if navigation is needed
		const slideCount = fullContainer.querySelectorAll('.swiper-slide').length

		// Get the gallery container
		const galleryContainer = document.querySelector('.gallery')

		// Initialize thumbnail swiper only if there's more than one image
		let galleryThumbs = null
		if (slideCount > 1) {
			// Calculate thumbnail size based on main gallery height
			// Formula: 3 × thumbHeight + 2 × gap = mainGalleryHeight
			// So: thumbHeight = (mainGalleryHeight - 2 × gap) / 3
			const gap = 10
			const mainGalleryHeight = fullContainer.offsetHeight
			const thumbHeight = (mainGalleryHeight - 2 * gap) / 3

			// First, make ALL slides square BEFORE Swiper initializes
			const allSlides = thumbsContainer.querySelectorAll('.swiper-slide')

			allSlides.forEach(slide => {
				slide.style.width = thumbHeight + 'px'
				slide.style.height = thumbHeight + 'px'
				slide.style.paddingTop = '0'
				slide.style.position = 'relative'
			})

			// Container height = 3 thumbnails + 2 gaps
			thumbsContainer.style.maxHeight = mainGalleryHeight + 'px'
			thumbsContainer.style.height = mainGalleryHeight + 'px'

			galleryThumbs = new Swiper(thumbsContainer, {
				spaceBetween: gap,
				slidesPerView: 'auto',
				freeMode: {
					enabled: true,
					sticky: false,
					momentumRatio: 0.5,
				},
				watchSlidesProgress: true,
				direction: 'vertical',
				slidesPerGroup: 1,
				autoHeight: false,
				watchOverflow: false,
				resistanceRatio: 0,
				slideToClickedSlide: true,
				allowTouchMove: true,
				touchRatio: 1,
				touchAngle: 45,
				grabCursor: true,
				mousewheel: {
					enabled: true,
					forceToAxis: true,
				},
				on: {
					init: function () {
						// FORCE square after init
						const makeSquare = () => {
							// Calculate size based on main gallery
							const gap = 10
							const mainGalleryHeight = fullContainer.offsetHeight
							const thumbHeight = (mainGalleryHeight - 2 * gap) / 3

							// Convert slides to array first
							Array.from(this.slides).forEach((slide, index) => {
								// FORCE height = width for SQUARE
								slide.style.setProperty('width', thumbHeight + 'px', 'important')
								slide.style.setProperty('height', thumbHeight + 'px', 'important')
								slide.style.setProperty('padding-top', '0', 'important')
								// Add gap between slides (but not after last visible one)
								if (index < this.slides.length - 1) {
									slide.style.setProperty('margin-bottom', gap + 'px', 'important')
								} else {
									slide.style.setProperty('margin-bottom', '0', 'important')
								}

								const slideImage = slide.querySelector('.slide__image')
								if (slideImage) {
									slideImage.style.setProperty('position', 'absolute', 'important')
									slideImage.style.setProperty('top', '0', 'important')
									slideImage.style.setProperty('left', '0', 'important')
									slideImage.style.setProperty('width', '100%', 'important')
									slideImage.style.setProperty('height', '100%', 'important')
									slideImage.style.setProperty('margin', '0', 'important')
									slideImage.style.setProperty('padding', '10%', 'important')
									slideImage.style.setProperty('display', 'flex', 'important')
									slideImage.style.setProperty('align-items', 'center', 'important')
									slideImage.style.setProperty('justify-content', 'center', 'important')
									slideImage.style.setProperty('box-sizing', 'border-box', 'important')
									slideImage.style.setProperty('overflow', 'visible', 'important')

									const img = slideImage.querySelector('img')
									if (img) {
										img.style.setProperty('width', '100%', 'important')
										img.style.setProperty('height', '100%', 'important')
										img.style.setProperty('object-fit', 'contain', 'important')
										img.style.setProperty('max-width', '100%', 'important')
										img.style.setProperty('max-height', '100%', 'important')
									}
								}
							})
						}
						makeSquare()
						setTimeout(makeSquare, 0)
						setTimeout(makeSquare, 10)
						setTimeout(makeSquare, 50)
						setTimeout(makeSquare, 100)
						setTimeout(makeSquare, 200)
						setTimeout(makeSquare, 500)
					},
					slideChange: function () {
						const gap = 10
						const mainGalleryHeight = fullContainer.offsetHeight
						const thumbHeight = (mainGalleryHeight - 2 * gap) / 3
						Array.from(this.slides).forEach((slide, index) => {
							slide.style.setProperty('width', thumbHeight + 'px', 'important')
							slide.style.setProperty('height', thumbHeight + 'px', 'important')
							if (index < this.slides.length - 1) {
								slide.style.setProperty('margin-bottom', gap + 'px', 'important')
							} else {
								slide.style.setProperty('margin-bottom', '0', 'important')
							}
						})
					},
					resize: function () {
						const gap = 10
						const mainGalleryHeight = fullContainer.offsetHeight
						const thumbHeight = (mainGalleryHeight - 2 * gap) / 3
						Array.from(this.slides).forEach((slide, index) => {
							slide.style.setProperty('width', thumbHeight + 'px', 'important')
							slide.style.setProperty('height', thumbHeight + 'px', 'important')
							if (index < this.slides.length - 1) {
								slide.style.setProperty('margin-bottom', gap + 'px', 'important')
							} else {
								slide.style.setProperty('margin-bottom', '0', 'important')
							}
						})
						thumbsContainer.style.maxHeight = mainGalleryHeight + 'px'
						thumbsContainer.style.height = mainGalleryHeight + 'px'
						this.update()
					},
				},
			})
		} else {
			// Add class to make full image take full width when only one image
			if (galleryContainer) {
				galleryContainer.classList.add('slider--with-1')
			}
		}

		// Initialize main gallery swiper
		const galleryFull = new Swiper(fullContainer, {
			spaceBetween: 10,
			navigation: {
				nextEl: '.section--gallery .icon--arrow-slider--next',
				prevEl: '.section--gallery .icon--arrow-slider--prev',
				hideOnClick: false,
			},
			thumbs: {
				swiper: galleryThumbs,
			},
			// Only enable navigation if there are multiple slides
			allowTouchMove: slideCount > 1,
			// Control navigation visibility
			on: {
				init: function () {
					updateNavigationVisibility(this)
				},
				slideChange: function () {
					updateNavigationVisibility(this)
				},
				resize: function () {
					updateNavigationVisibility(this)
				},
			},
		})

		/**
		 * Update navigation button visibility based on current slide and total slides
		 * @param {Swiper} swiper - The Swiper instance
		 */
		function updateNavigationVisibility(swiper) {
			const prevButton = document.querySelector('.section--gallery .icon--arrow-slider--prev')
			const nextButton = document.querySelector('.section--gallery .icon--arrow-slider--next')

			if (!prevButton || !nextButton) return

			// Hide both buttons if only one slide
			if (swiper.slides.length <= 1) {
				prevButton.style.display = 'none'
				nextButton.style.display = 'none'
				return
			}

			// Show/hide prev button
			if (swiper.isBeginning) {
				prevButton.style.display = 'none'
			} else {
				prevButton.style.display = 'block'
			}

			// Show/hide next button
			if (swiper.isEnd) {
				nextButton.style.display = 'none'
			} else {
				nextButton.style.display = 'block'
			}
		}

		// Store Swiper instances globally for gallery toggle functionality
		window.galleryThumbs = galleryThumbs
		window.galleryFull = galleryFull
	}

	/**
	 * Initialize gallery toggle functionality
	 */
	function initGalleryToggle() {
		const toggleButtons = document.querySelectorAll('.gallery__full .gallery__toggle')

		toggleButtons.forEach(button => {
			button.addEventListener('click', function (event) {
				event.preventDefault()

				// Toggle fullwidth class on gallery section
				const gallerySection = document.querySelector('.section--gallery')
				if (gallerySection) {
					gallerySection.classList.toggle('fullwidth')

					// Toggle hidden class on previous section
					const prevSection = gallerySection.previousElementSibling
					if (prevSection && prevSection.tagName === 'SECTION') {
						prevSection.classList.toggle('hidden')
					}

					// Update Swiper instances if they exist
					if (window.galleryThumbs && typeof window.galleryThumbs.update === 'function') {
						window.galleryThumbs.update()
					}
					if (window.galleryFull && typeof window.galleryFull.update === 'function') {
						window.galleryFull.update()
					}
				}
			})
		})
	}
})()
